import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, MapPin, Plane, Building2, Car, Home, Sparkles, FileText, HelpCircle, CreditCard, Briefcase } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Employee {
  id: string;
  company_id: string;
  current_country?: string;
  companies: {
    name: string;
  };
}

interface Profile {
  first_name: string;
  last_name: string;
  residence_country?: string;
}

interface Points {
  total_points: number;
  used_points: number;
}

interface FeaturedDestination {
  id: string;
  name: string;
  photo_url?: string;
  points_required: number;
  description?: string;
  category: string;
}

interface HomeTabProps {
  employee: Employee | null;
  profile: Profile | null;
  currentPoints: Points | null;
  destinations: FeaturedDestination[];
  onRequestSubmitted: () => void;
}

const HomeTab = ({ employee, profile, currentPoints, destinations, onRequestSubmitted }: HomeTabProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newRequest, setNewRequest] = useState({
    destination_country: "",
    start_date: "",
    end_date: "",
    comment: ""
  });
  const { toast } = useToast();

  const calculateWeeks = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return Math.ceil(diffDays / 7);
  };

  const handleSubmitRequest = async () => {
    if (!newRequest.destination_country || !newRequest.start_date || !newRequest.end_date) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires"
      });
      return;
    }

    const startDate = new Date(newRequest.start_date);
    const endDate = new Date(newRequest.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (startDate < today) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "La date de début ne peut pas être dans le passé"
      });
      return;
    }

    if (endDate < startDate) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "La date de fin doit être après la date de début"
      });
      return;
    }

    const requiredPoints = calculateWeeks(newRequest.start_date, newRequest.end_date);
    
    if (!currentPoints || (currentPoints.total_points - currentPoints.used_points) < requiredPoints) {
      toast({
        variant: "destructive", 
        title: "Points insuffisants",
        description: `Cette demande nécessite ${requiredPoints} point(s), mais vous n'avez que ${currentPoints ? currentPoints.total_points - currentPoints.used_points : 0} point(s) disponible(s).`
      });
      return;
    }

    try {
      const { error: requestError } = await supabase
        .from('remote_work_requests')
        .insert({
          employee_id: employee?.id,
          company_id: employee?.company_id,
          destination_country: newRequest.destination_country,
          start_date: newRequest.start_date,
          end_date: newRequest.end_date,
          requested_points: requiredPoints,
          comment: newRequest.comment || null,
          status: 'pending'
        });

      if (requestError) throw requestError;

      toast({
        title: "Demande envoyée",
        description: `Votre demande de télétravail (${requiredPoints} point(s)) a été envoyée pour validation`
      });

      setIsDialogOpen(false);
      setNewRequest({ destination_country: "", start_date: "", end_date: "", comment: "" });
      onRequestSubmitted();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible d'envoyer la demande"
      });
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const services = [
    { name: "Obtention visa", icon: FileText },
    { name: "Aide à l'installation", icon: HelpCircle },
    { name: "Compte bancaire", icon: CreditCard },
    { name: "Trouver un logement", icon: Home },
    { name: "Conseil fiscal", icon: Briefcase }
  ];

  const travelOptions = [
    { name: "Louer mon appart", partner: "Rehome", icon: Home },
    { name: "Louer ma voiture", partner: "Getaround", icon: Car },
    { name: "Nettoyage logement", partner: "Wecasa", icon: Sparkles }
  ];

  return (
    <div className="space-y-6 sm:space-y-8 overflow-x-hidden">
      {/* Welcome Section */}
      <div className="text-center mb-6 sm:mb-8 px-4">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-2 sm:mb-4 leading-tight">
          Bienvenue {profile?.first_name}
        </h1>
        <p className="text-sm sm:text-lg text-muted-foreground flex items-center justify-center gap-2 flex-wrap">
          <Building2 className="h-4 w-4 flex-shrink-0" />
          <span className="break-words">{employee?.companies?.name || 'Votre entreprise'}</span>
        </p>
      </div>

      {/* Points & Location Cards */}
      <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 px-4">
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl">
              <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0"></div>
              <span className="break-words">Solde Nest Points</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {currentPoints ? (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-1">
                    {currentPoints.total_points - currentPoints.used_points}
                  </div>
                  <div className="text-sm text-muted-foreground">Points disponibles</div>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Points utilisés: {currentPoints.used_points}</span>
                  <span>Total: {currentPoints.total_points}</span>
                </div>
                <div className="w-full bg-muted h-2 rounded-full">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ 
                      width: `${(currentPoints.used_points / currentPoints.total_points) * 100}%` 
                    }}
                  />
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">Chargement des points...</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              <span className="break-words">Ma localisation</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3 sm:space-y-2">
              <div>
                <span className="font-medium text-sm sm:text-base">Pays de résidence:</span>
                <p className="text-muted-foreground text-sm sm:text-base break-words">{profile?.residence_country || 'Non défini'}</p>
              </div>
              <div>
                <span className="font-medium text-sm sm:text-base">Actuellement:</span>
                <p className="text-muted-foreground text-sm sm:text-base break-words">{employee?.current_country || profile?.residence_country || 'Non défini'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CTA Button */}
      <div className="text-center mb-6 sm:mb-8 px-4">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              size="lg" 
              className="w-full sm:w-auto px-4 sm:px-8 py-4 sm:py-6 text-sm sm:text-lg font-bold bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl transition-all"
            >
              <Plus className="h-4 w-4 sm:h-6 sm:w-6 mr-2 sm:mr-3 flex-shrink-0" />
              <span className="text-center break-words leading-tight">
                <span className="block sm:inline">Faire une nouvelle demande</span>
                <span className="block sm:inline"> de Workation</span>
              </span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md mx-4 sm:mx-auto">
            <DialogHeader className="text-left">
              <DialogTitle className="text-lg sm:text-xl break-words">Nouvelle demande de télétravail</DialogTitle>
              <DialogDescription className="text-sm sm:text-base break-words">
                Les points nécessaires seront calculés automatiquement (1 semaine = 1 point)
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="country">Pays de destination *</Label>
                <Input
                  id="country"
                  type="text"
                  value={newRequest.destination_country}
                  onChange={(e) => setNewRequest(prev => ({...prev, destination_country: e.target.value}))}
                  placeholder="Ex: Portugal, Espagne, Italie..."
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Date de début *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    min={getMinDate()}
                    value={newRequest.start_date}
                    onChange={(e) => setNewRequest(prev => ({...prev, start_date: e.target.value}))}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">Date de fin *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    min={newRequest.start_date || getMinDate()}
                    value={newRequest.end_date}
                    onChange={(e) => setNewRequest(prev => ({...prev, end_date: e.target.value}))}
                    required
                  />
                </div>
              </div>
              {newRequest.start_date && newRequest.end_date && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm">
                    <span className="font-medium">Durée:</span> {calculateWeeks(newRequest.start_date, newRequest.end_date)} semaine(s)
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Points nécessaires:</span> {calculateWeeks(newRequest.start_date, newRequest.end_date)} point(s)
                  </p>
                </div>
              )}
              <div>
                <Label htmlFor="comment">Commentaire (optionnel)</Label>
                <Textarea
                  id="comment"
                  value={newRequest.comment}
                  onChange={(e) => setNewRequest(prev => ({...prev, comment: e.target.value}))}
                  placeholder="Raison de la demande, informations complémentaires..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSubmitRequest}>
                Envoyer la demande
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Destinations Carousel */}
      <Card className="mx-4">
        <CardHeader className="pb-3 sm:pb-6">
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Plane className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
            <span className="break-words">Destinations tendances</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {destinations.map((destination) => (
              <div key={destination.id} className="group cursor-pointer">
                <div className="relative overflow-hidden rounded-lg aspect-[4/3] mb-3">
                  <img 
                    src={destination.photo_url || 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=800&auto=format&fit=crop&q=60&fm=jpg'} 
                    alt={destination.name}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=800&auto=format&fit=crop&q=60&fm=jpg';
                    }}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-2 left-2 text-white">
                    <h3 className="font-semibold">{destination.name}</h3>
                    <p className="text-sm opacity-90">{destination.points_required} points/mois</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">{destination.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Services Carousel */}
      <Card className="mx-4">
        <CardHeader className="pb-3 sm:pb-6">
          <CardTitle className="text-lg sm:text-xl break-words">Nos options pour voyager l'esprit léger</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
            {services.map((service, index) => (
              <div key={index} className="text-center p-3 sm:p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                <service.icon className="h-6 w-6 sm:h-8 sm:w-8 text-primary mx-auto mb-2 sm:mb-3" />
                <p className="text-xs sm:text-sm font-medium text-foreground break-words leading-tight">{service.name}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Travel Options */}
      <Card className="mx-4">
        <CardHeader className="pb-3 sm:pb-6">
          <CardTitle className="text-lg sm:text-xl break-words">Prêt pour le grand départ ?</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {travelOptions.map((option, index) => (
              <div key={index} className="flex items-center p-3 sm:p-4 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer">
                <option.icon className="h-6 w-6 sm:h-8 sm:w-8 text-primary mr-2 sm:mr-3 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm sm:text-base break-words">{option.name}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground break-words">via {option.partner}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HomeTab;